import { useMemo } from "react";

function hash(str) {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

function mulberry32(seed) {
    return function () {
        seed |= 0;
        seed = (seed + 0x6D2B79F5) | 0;
        let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

const GOLD = "#EAB308";
const BLUE = "#38BDF8";
const GRID = "rgba(255,255,255,0.055)";

export const CoverArt = ({ seed = "investidor", variant = "line", className = "" }) => {
    const art = useMemo(() => {
        const rnd = mulberry32(hash(seed));
        const W = 400, H = 250;

        const grid = [];
        for (let x = 0; x <= W; x += 40) grid.push(<line key={`v${x}`} x1={x} y1={0} x2={x} y2={H} stroke={GRID} strokeWidth="1" />);
        for (let y = 0; y <= H; y += 40) grid.push(<line key={`h${y}`} x1={0} y1={y} x2={W} y2={y} stroke={GRID} strokeWidth="1" />);

        const pts = [];
        const n = 14;
        let level = 60 + rnd() * 40;
        for (let i = 0; i <= n; i++) {
            level += (rnd() - 0.42) * 22;
            level = Math.max(18, Math.min(95, level));
            pts.push([20 + (i * (W - 40)) / n, H - (level / 100) * (H - 30)]);
        }
        const linePath = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
        const areaPath = `${linePath} L${pts[pts.length - 1][0]},${H} L${pts[0][0]},${H} Z`;

        const candles = [];
        let cLevel = 120 + rnd() * 60;
        for (let i = 0; i < 11; i++) {
            const x = 30 + i * ((W - 60) / 10);
            const up = rnd() > 0.45;
            const body = 14 + rnd() * 34;
            cLevel += up ? -(10 + rnd() * 14) : 8 + rnd() * 12;
            cLevel = Math.max(50, Math.min(H - 50, cLevel));
            const color = up ? GOLD : BLUE;
            candles.push(<line key={`w${i}`} x1={x} y1={cLevel - body / 2 - 12 * rnd()} x2={x} y2={cLevel + body / 2 + 12 * rnd()} stroke={color} strokeWidth="1.4" opacity="0.7" />);
            candles.push(<rect key={`b${i}`} x={x - 7} y={cLevel - body / 2} width={14} height={body} fill={up ? color : "transparent"} stroke={color} strokeWidth="1.4" opacity={up ? 0.9 : 0.8} />);
        }

        const bars = [];
        for (let i = 0; i < 12; i++) {
            const bh = 20 + i * 12 + rnd() * 26;
            const x = 24 + i * ((W - 48) / 12);
            bars.push(<rect key={`bar${i}`} x={x} y={H - 10 - bh} width={20} height={bh} fill={i % 3 === 2 ? GOLD : "rgba(255,255,255,0.14)"} />);
        }

        const pulse = [];
        let px = 0, py = H * 0.6;
        pulse.push(`M0,${py}`);
        while (px < W) {
            const seg = 24 + rnd() * 30;
            px += seg;
            const spike = rnd() > 0.72;
            py = spike ? H * (0.2 + rnd() * 0.25) : H * (0.5 + rnd() * 0.3);
            pulse.push(`L${px},${py}`);
        }

        const id = hash(seed) % 100000;
        return { grid, linePath, areaPath, pts, candles, bars, pulse: pulse.join(" "), id };
    }, [seed]);

    return (
        <svg viewBox="0 0 400 250" preserveAspectRatio="xMidYMid slice" className={`h-full w-full ${className}`} aria-hidden="true">
            <defs>
                <linearGradient id={`fade-${art.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={GOLD} stopOpacity="0.22" />
                    <stop offset="100%" stopColor={GOLD} stopOpacity="0" />
                </linearGradient>
                <radialGradient id={`glow-${art.id}`} cx="75%" cy="20%" r="80%">
                    <stop offset="0%" stopColor={BLUE} stopOpacity="0.08" />
                    <stop offset="100%" stopColor="#0A0E1A" stopOpacity="0" />
                </radialGradient>
            </defs>
            <rect width="400" height="250" fill="#0A0E1A" />
            <rect width="400" height="250" fill={`url(#glow-${art.id})`} />
            {art.grid}
            {variant === "line" && (
                <>
                    <path d={art.areaPath} fill={`url(#fade-${art.id})`} />
                    <path d={art.linePath} fill="none" stroke={GOLD} strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" />
                    {art.pts.filter((_, i) => i % 3 === 0).map((p, i) => (
                        <circle key={i} cx={p[0]} cy={p[1]} r="3" fill="#060913" stroke={GOLD} strokeWidth="1.6" />
                    ))}
                    <circle cx={art.pts[art.pts.length - 1][0]} cy={art.pts[art.pts.length - 1][1]} r="4.5" fill={GOLD} />
                    <circle cx={art.pts[art.pts.length - 1][0]} cy={art.pts[art.pts.length - 1][1]} r="10" fill="none" stroke={GOLD} strokeOpacity="0.35" />
                </>
            )}
            {variant === "candles" && art.candles}
            {variant === "bars" && (
                <>
                    <line x1="0" y1="240" x2="400" y2="240" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
                    {art.bars}
                </>
            )}
            {variant === "pulse" && (
                <path d={art.pulse} fill="none" stroke={BLUE} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
            )}
        </svg>
    );
};
